import { Alert, Box, Button, Stack, Tab, Tabs } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent, TrackingEventTypes } from '../../common/tracking';
import { fetchPatient } from '../../store/patientsSlice';
import { AppDispatch, RootState } from '../../store/store';
import { RoleBasedContent } from '../RoleBasedContent';
import { PageBox, PatientStatusChip } from '../ui/DaliaComponents';
import { PatientInclusionInfo } from './PatientInfoInclusion';
import PatientInfoVisits from './PatientInfoVisits';

export const PatientInfo: React.FC = () => {
  const { id, tab } = useParams<{ id: string; tab: string }>();
  const dispatch: AppDispatch = useDispatch();
  const tabs = ['inclusion', 'visits'];
  const currentTab = tabs.includes(tab || '') ? tab : tabs[0];
  const [force, setForce] = useState(false);

  const { patientEdited, loading, error } = useSelector((state: RootState) => state.patients);
  const needsRefetch = useMemo(() => !patientEdited || patientEdited.id !== id, [patientEdited, id]);

  useEffect(() => {
    if (patientEdited) trackEvent(TrackingEventTypes.VIEW_PATIENT_INFO, { patient_id: patientEdited.patientId });
  }, [patientEdited]);

  const handleTabChange = (newTab: string) => {
    if (patientEdited) navigate(`/patient/${patientEdited.id}/view/${newTab}`);
  };

  /**
   * Force the display of the patient page
   */
  const handleForceClick = () => {
    setForce(true);
  };

  useEffect(() => {
    if (id && needsRefetch) {
      void dispatch(fetchPatient({ id: id }));
    }
  }, [dispatch, id, needsRefetch]);

  const navigate = useNavigate();

  const addVisit = () => {
    if (patientEdited) navigate(`/patient/${patientEdited.id}/new-visit`);
  };
  const goToPatientCompletePage = () => {
    if (patientEdited) navigate(`/patient/${patientEdited.id}/complete`);
  };

  if (loading || !patientEdited) {
    return <div>Loading...</div>;
  }

  return (
    <PageBox
      title={`Fiche patient nº${patientEdited.patientId} ${patientEdited.externalId ? '- ' + patientEdited.externalId : ''} - créé le ${patientEdited.createdAt && format(new Date(patientEdited.createdAt), "dd MMM yyyy HH'h'mm")}`}
    >
      <Grid container>
        <Grid display="flex" justifyContent="right" size="grow">
          <Stack direction="row" spacing={2}>
            {patientEdited && <PatientStatusChip patient={patientEdited} />}
          </Stack>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {patientEdited && (patientEdited.inclusionInfo || force) ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              onChange={(_event, newValue) => {
                handleTabChange(newValue);
              }}
              value={currentTab}
            >
              <Tab label="Information d'inclusion" value={tabs[0]} />
              <Tab label="Visites ultérieures" value={tabs[1]} />
            </Tabs>
          </Box>
          {currentTab === tabs[0] && <PatientInclusionInfo editMode={true} patient={patientEdited} />}
          {currentTab === tabs[1] &&
            (patientEdited.visits ? (
              <PatientInfoVisits patient={patientEdited} />
            ) : (
              <Grid container sx={{ mt: 5 }}>
                <Grid offset={2} size={8}>
                  <Stack direction="column" spacing={2}>
                    <Alert icon={false} sx={{ bgcolor: 'primary.light', color: 'primary.main', fontSize: '1rem' }}>
                      Aucune visite suite à l&apos;inclusion n&apos;a été enregistrée pour ce patient
                    </Alert>
                    <Button color="primary" onClick={addVisit} variant="contained">
                      + Nouvelle visite
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            ))}
        </>
      ) : (
        <Grid container>
          <Grid size={3}></Grid>
          <Grid size={6}>
            <Stack direction="column" spacing={2}>
              <Alert icon={false} sx={{ bgcolor: 'primary.light', color: 'primary.main', fontSize: 'large' }}>
                Le patient a été créé mais les informations d&apos;inclusion ne sont pas encore completées. Cliquer ci-dessous pour les completer
              </Alert>
              <Button color="primary" onClick={goToPatientCompletePage} variant="contained">
                Ajouter les informations d&apos;inclusion
              </Button>

              <RoleBasedContent
                component={
                  <a href="#" onClick={handleForceClick} style={{ color: 'blue', textDecoration: 'underline' }}>
                    Forcer l&apos;affichage de la fiche patient (admin)
                  </a>
                }
                roles={['admin']}
              />
            </Stack>
          </Grid>
        </Grid>
      )}
    </PageBox>
  );
};

export default PatientInfo;
